<template>
    <div class="waba-activity-list-view" ref="activityListView">
        <a-spin :spinning="loading && activities.length === 0">
            <template v-if="activities.length > 0">
                <div class="date-group" :key="date" v-for="date of Object.keys(groups())">
                    <div class="date-header">
                        <div class="date-badge">
                            <span class="material-icons">event</span>
                            <span>{{ $moment(date, "YYYY-MM-DD").format("LL") }}</span>
                        </div>
                        <div class="date-line"></div>
                    </div>
                    <div class="activity-cards">
                        <WabaActivityCard :activity="activity" :key="activity._id" v-for="activity of groups()[date]" />
                    </div>
                </div>
                
                <!-- Load More Button -->
                <div class="load-more-container">
                    <button 
                        class="load-more-btn" 
                        :class="{ disabled: !hasMore || loading }"
                        :disabled="!hasMore || loading"
                        @click="handleLoadMore"
                    >
                        <a-spin v-if="loading" size="small" />
                        <template v-else>
                            <span class="material-icons">{{ hasMore ? 'expand_more' : 'check_circle' }}</span>
                            <span>{{ hasMore ? 'Daha Fazla Yükle' : 'Tüm Aktiviteler Yüklendi' }}</span>
                        </template>
                    </button>
                </div>
            </template>
            <div class="empty-activities" v-else-if="!loading">
                <div class="empty-icon">
                    <span class="material-icons">history</span>
                </div>
                <div class="empty-title">Henüz Aktivite Yok</div>
                <div class="empty-desc">
                    Bu kanal için henüz bir webhook aktivitesi kaydedilmemiş.
                    Meta'dan webhook alındığında aktiviteler burada görüntülenecektir.
                </div>
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import WabaActivityCard from "./WabaActivityCard.vue";

export default Vue.extend({
    name: "WabaActivityListView",
    components: { WabaActivityCard },
    props: {
        activities: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        hasMore: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            groupByDate: true,
            moment,
        };
    },
    methods: {
        groups() {
            const groups = {};
            const orderedItems = _.orderBy(this.activities, ["createdAt"], ["desc"]);
            orderedItems.forEach((activity) => {
                const date = moment(this.groupByDate ? activity.createdAt : undefined).format("YYYY-MM-DD");
                if (!groups[date]) {
                    groups[date] = [];
                }
                groups[date].push(activity);
            });
            return groups;
        },
        handleLoadMore() {
            if (!this.loading && this.hasMore) {
                this.$emit("load-more");
            }
        },
    },
});
</script>

<style scoped lang="scss">
.waba-activity-list-view {
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 100%;
    overflow: auto;
    padding-right: 8px;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: var(--color-border-default);
        border-radius: 3px;
        
        &:hover {
            background: var(--color-border-strong);
        }
    }
    
    .empty-activities {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        text-align: center;
        
        .empty-icon {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: var(--color-bg-neutral-subtle);
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 20px;
            
            .material-icons {
                font-size: 36px;
                color: var(--color-text-secondary);
            }
        }
        
        .empty-title {
            font-size: 18px;
            font-weight: 600;
            color: var(--color-text-default);
            margin-bottom: 8px;
        }
        
        .empty-desc {
            font-size: 14px;
            color: var(--color-text-secondary);
            max-width: 400px;
            line-height: 1.5;
        }
    }

    .load-more-container {
        display: flex;
        justify-content: center;
        padding: 20px 0;
        
        .load-more-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 12px 24px;
            border: 1px solid var(--color-border-default);
            border-radius: 8px;
            background: var(--color-bg-default);
            color: var(--color-text-default);
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 220px;
            
            .material-icons {
                font-size: 20px;
            }
            
            &:hover:not(.disabled) {
                background: var(--color-bg-neutral-subtle);
                border-color: var(--color-border-strong);
            }
            
            &.disabled {
                cursor: not-allowed;
                opacity: 0.6;
                
                .material-icons {
                    color: #10b981;
                }
            }
        }
    }

    .date-group {
        .date-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 16px;
            
            .date-badge {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 12px;
                background: var(--color-bg-neutral-subtle);
                border-radius: 20px;
                font-size: 13px;
                font-weight: 500;
                color: var(--color-text-default);
                white-space: nowrap;
                
                .material-icons {
                    font-size: 16px;
                    color: var(--color-icon-default);
                }
            }
            
            .date-line {
                flex: 1;
                height: 1px;
                background: var(--color-border-default);
            }
        }

        .activity-cards {
            display: flex;
            flex-direction: column;
        }
    }
}
</style>
